import { FormEvent, useEffect, useState } from 'react';
import type { OutletRef, ProductListItem, QuickButton } from '@kassa/shared';
import { api, ApiError } from '../../api';
import { formatMoney } from '../../format';

const MAX = 24;

// Tez düymələr (KT-101): kassada barkodsuz mal üçün bir toxunuşla səbətə. Obyekt üzrə, sıra ilə; ən çox 24.
export function QuickButtonsPanel() {
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [outletId, setOutletId] = useState('');
  const [items, setItems] = useState<QuickButton[]>([]);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<ProductListItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    api
      .outlets()
      .then((o) => {
        setOutlets(o);
        if (o[0]) setOutletId(o[0].id);
      })
      .catch(() => setError('Obyektlər yüklənmədi'));
  }, []);

  useEffect(() => {
    if (!outletId) return;
    api
      .quickButtonsSettings(outletId)
      .then(setItems)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Düymələr yüklənmədi'));
  }, [outletId]);

  async function search(e: FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    try {
      setFound((await api.products({ q: q.trim(), limit: 8 })).items.filter((p) => p.active));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Axtarış alınmadı');
    }
  }

  const add = (p: ProductListItem) => {
    if (items.some((x) => x.productId === p.id) || items.length >= MAX) return;
    setItems([...items, { position: items.length + 1, productId: p.id, name: p.name, unit: p.unitCode, retailPrice: p.retailPrice }]);
    setFound([]);
    setQ('');
  };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setItems(await api.quickButtonsSave(outletId, items.map((x) => x.productId)));
      setNotice('Tez düymələr saxlanıldı.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Saxlanmadı');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card card-wide" aria-label="Tez düymələr">
      <h2>Tez düymələr</h2>
      <p className="muted small">Barkodsuz mallar (çörək, tərəvəz) kassada bir toxunuşla səbətə düşür. Ən çox {MAX} düymə.</p>
      {outlets.length > 1 && (
        <label className="field">
          Obyekt
          <select value={outletId} onChange={(e) => setOutletId(e.target.value)}>
            {outlets.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <form onSubmit={search} role="search" className="toolbar">
        <input className="search" aria-label="Düyməyə mal" placeholder="Malın adı" value={q} onChange={(e) => setQ(e.target.value)} disabled={items.length >= MAX} />
        <button type="submit" className="btn-small" disabled={items.length >= MAX}>
          Tap
        </button>
      </form>
      {found.length > 0 && (
        <ul className="product-list">
          {found.map((p) => (
            <li key={p.id}>
              <button type="button" className="product-row" onClick={() => add(p)}>
                <span className="product-name">{p.name}</span>
                <span className="mono">{formatMoney(p.retailPrice)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <ol className="quick-edit">
        {items.map((x, i) => (
          <li key={x.productId}>
            <span className="product-name">{x.name}</span>
            <span className="mono">{formatMoney(x.retailPrice)}</span>
            <button type="button" className="btn-small" aria-label={`${x.name} — yuxarı`} onClick={() => move(i, -1)} disabled={i === 0}>
              ↑
            </button>
            <button type="button" className="btn-small" aria-label={`${x.name} — aşağı`} onClick={() => move(i, 1)} disabled={i === items.length - 1}>
              ↓
            </button>
            <button type="button" className="link-btn" onClick={() => setItems(items.filter((_, k) => k !== i))}>
              Sil
            </button>
          </li>
        ))}
      </ol>
      {items.length === 0 && <p className="muted">Düymə yoxdur.</p>}
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="ok" role="status">
          {notice}
        </p>
      )}
      <button type="button" className="btn-primary" onClick={() => void save()} disabled={busy || !outletId}>
        Düymələri saxla
      </button>
    </section>
  );
}
